import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

/**
 * A searchable tick list of the distributor's products, for choosing
 * which ones a shop gets. `items`: [{product_id, name, nafdac_number,
 * unit}]; `selected`: Set of product ids; `onChange(nextSet)`.
 */
export default function ProductTicker({ items, selected, onChange, maxHeight = 'max-h-[50vh]' }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((i) => `${i.name} ${i.nafdac_number || ''}`.toLowerCase().includes(term));
  }, [items, q]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };
  const allShown = shown.length > 0 && shown.every((i) => selected.has(i.product_id));
  const toggleShown = () => {
    const next = new Set(selected);
    shown.forEach((i) => (allShown ? next.delete(i.product_id) : next.add(i.product_id)));
    onChange(next);
  };

  if (!items.length) {
    return <p className="text-sm text-gray-600 py-3" data-testid="ticker-empty">{t('shopProducts.noProducts')}</p>;
  }
  return (
    <div>
      <div className="relative mb-2">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-9 bg-white"
               placeholder={t('shopProducts.search')} data-testid="ticker-search" />
      </div>
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="font-semibold" data-testid="ticker-count">
          {t('shopProducts.selected', { n: selected.size, total: items.length })}
        </span>
        <button type="button" onClick={toggleShown} className="font-semibold text-farm-primary"
                data-testid="ticker-toggle-all">
          {allShown ? t('shopProducts.untickAll') : t('shopProducts.tickAll')}
        </button>
      </div>
      <ul className={`divide-y rounded-xl border bg-white overflow-y-auto ${maxHeight}`} data-testid="ticker-list">
        {shown.map((item) => (
          <li key={item.product_id}>
            <label className="flex items-center gap-3 px-3 py-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5 accent-[#2e7d32] shrink-0" checked={selected.has(item.product_id)}
                     onChange={() => toggle(item.product_id)} data-testid={`ticker-item-${item.product_id}`} />
              <span className="min-w-0">
                <span className="block font-medium break-words">{item.name}</span>
                {(item.nafdac_number || item.unit) && (
                  <span className="block text-xs text-gray-500">
                    {[item.nafdac_number && `NAFDAC ${item.nafdac_number}`, item.unit].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
